import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Form, Loader, Modal, Schema } from "rsuite";
import { ApiFinance } from "../core/apis";
import { ApiInvoice } from "../core/apis/invoice";
import { ActionsModal } from "../core/redux/slices/sliceModal";
import { EnumModals } from "../libs";
import { DoesObjectHasAnyInvalidValue } from "../utils/formUtils";
import { ModalData } from "./modal";
import { TextAreaInput, TextInput } from "./textInput.component";
import { InvoiceModal } from "./viewInvoice.component";
import { ApiSchemaEmailBody } from "../models/api";
import { FiMail, FiCheck } from 'react-icons/fi';

type MailFormData = {
  to: string;
  subject: string;
  message: string;
  cc?: string;
  bcc?: string;
};

export const SendMail = ({
  value,
  fromTab,
  invoiceId,
  alreadySent,
  defaultEmail,
  ccEmail,
  handleSent,
  invoiceStartDate,
  invoiceEndDate,
  haveEmail,
  defaultSubject,
  defaultMessage,
  messageGenerator,
}: {
  value?: any;
  fromTab?: string;
  invoiceId: string;
  alreadySent: boolean;
  defaultEmail?: string;
  ccEmail?: string;
  handleSent?: () => void;
  invoiceStartDate: string;
  invoiceEndDate: string;
  haveEmail: boolean;
  defaultSubject?: string;
  defaultMessage?: string;
  messageGenerator?: (selectedDate: Date) => string;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const [invoiceYearMonth, setInvoiceYearMonth] = useState<Date>();
  const [clickedEmailOptions, setClickedEmailOptions] = useState({
    cc: false,
    bcc: false,
  });
  const [formData, setFormData] = useState<MailFormData>({
    to: defaultEmail || "",
    subject: defaultSubject || "",
    message: defaultMessage || "",
  });
  const emailClassName = (() => {
    let className = "me-2";
    if (alreadySent) {
      className += " fas fa-check text-green-200";
    } else {
      className += " far fa-envelope";
      if (haveEmail) {
        className += " text-green-200 click";
      } else {
        className += " text-black-50";
      }
    }
    return className;
  })();
  const emailTooltip = (() => {
    let title = "";
    if (alreadySent) {
      title = "Email is already sent";
    } else {
      title = "Send email to manager";
      if (!haveEmail) {
        title = "Add email address from settings";
      }
    }
    return title;
  })();

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = useCallback(
    (data: any, _: any) => {
      // Only update the changed fields, preserve other values
      setFormData((prevFormData) => ({
        ...prevFormData,
        ...data,
        subject: data.subject !== undefined ? data.subject : prevFormData.subject,
        message: data.message !== undefined ? data.message : prevFormData.message
      }));
    },
    []
  );

  const handleSubmit = async () => {
    const splitRegEx = /[,;]+/;
    const trimRegEx = /\s/g;
    const emailBody = {
      to: formData.to.replace(trimRegEx, "").split(splitRegEx),
      subject: formData.subject,
      message: formData.message,
      invoiceSKey: value?.sKey ?? "",
      yearMonth: invoiceYearMonth?.toISOString()!,
    } as ApiSchemaEmailBody;

    if (formData.cc) {
      emailBody.ccList = formData.cc.replace(trimRegEx, "").split(splitRegEx);
    }
    if (formData.bcc) {
      emailBody.bCcList = formData.bcc.replace(trimRegEx, "").split(splitRegEx);
    }
    if (fromTab === "patientFinance" || fromTab === "caregiverFinance") {
      emailBody.isPatientReport = fromTab === "patientFinance";
      await ApiFinance.SendEmail(emailBody);
      handleSent?.();
    } else {
      await ApiInvoice.SendEmail(emailBody);
    }

    setIsLoading(false);
    setOpen(false);
  };
  const handleEmailOptionsClick = (option: "cc" | "bcc") => {
    if (option === "cc") {
      setClickedEmailOptions({ ...clickedEmailOptions, cc: true });
    } else {
      setClickedEmailOptions({ ...clickedEmailOptions, bcc: true });
    }
  };

  useEffect(() => {
    const { cc, bcc, ...rest } = formData;
    const isEmpty = Object.values({ ...rest }).some(
      DoesObjectHasAnyInvalidValue
    );
    setIsDisabled(isEmpty);
  }, [formData]);

  const handleEmailClick = () => {
    ModalData.InvoiceDownloadModal.startDate = invoiceStartDate;
    ModalData.InvoiceDownloadModal.endDate = invoiceEndDate;
    ModalData.InvoiceDownloadModal.OkButtonText = "Done";
    ModalData.InvoiceDownloadModal.handleDownload = (
      selectedYearMonth: Date
    ) => {
      setInvoiceYearMonth(selectedYearMonth);
      if(messageGenerator) {
        setFormData((prev) => ({
          ...prev,
          message: messageGenerator(selectedYearMonth),
        }));
      }
      dispatch(ActionsModal.hideModal(EnumModals.INVOICE_DOWNLOAD_MODAL));
      setOpen(true);
    };

    dispatch(ActionsModal.openModal(EnumModals.INVOICE_DOWNLOAD_MODAL));
  };

  useEffect(() => {
    if (open) {
      return;
    }
    let message = defaultMessage || "";
    if(invoiceYearMonth && messageGenerator) {
      message = messageGenerator(invoiceYearMonth);
    }
    const resetOptions = { cc: false, bcc: false };
    const resetData: MailFormData = {
      to: defaultEmail || "",
      subject: defaultSubject || "",
      message,
    };
    if (ccEmail) {
      resetOptions.cc = true;
      resetData.cc = ccEmail;
    }
    setClickedEmailOptions(resetOptions);
    setFormData(resetData);
  }, [open, defaultEmail, ccEmail, defaultSubject, defaultMessage, invoiceYearMonth]);

  useEffect(() => {
    setClickedEmailOptions((previous) => {
      return { ...previous, cc: true };
    });
    setFormData((previous) => {
      return { ...previous, cc: ccEmail };
    });
  }, [ccEmail]);

  return (
    <>
      {isLoading ? (
        <Loader size="sm" />
      ) : (
        <button
          className={`btn btn-sm ${alreadySent ? 'btn-success' : haveEmail ? 'btn-outline-success' : 'btn-outline-secondary'} me-2`}
          onClick={() => {
            if (haveEmail) {
              handleEmailClick();
            }
          }}
          title={emailTooltip}
          disabled={!haveEmail}
        >
          {alreadySent ? <FiCheck /> : <FiMail />}
        </button>
      )}
      <Modal open={open} onClose={handleClose}>
        <Modal.Header>
          <Modal.Title>Send Invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            className="row"
            onChange={handleChange}
            onSubmit={handleSubmit}
            fluid
            formValue={formData}
            model={Schema.Model({
              toEmail: Schema.Types.StringType(),
              fromEmail: Schema.Types.StringType(),
              subject: Schema.Types.StringType(),
              message: Schema.Types.StringType(),
            })}
          >
            <div className="col-12 px-4 add-page-ctl">
              <div className="row">
                <div className="email-badge-wrapper">
                  <TextInput name="to" required fullWidth value={formData.to}>
                    To
                  </TextInput>
                  <div className="email-badge">
                    {!clickedEmailOptions?.cc && (
                      <span
                        className="badge bg-light text-dark m-1 click"
                        onClick={() => handleEmailOptionsClick("cc")}
                      >
                        Cc
                      </span>
                    )}
                    {!clickedEmailOptions?.bcc && (
                      <span
                        className="badge bg-light text-dark m-1 click"
                        onClick={() => handleEmailOptionsClick("bcc")}
                      >
                        Bcc
                      </span>
                    )}
                  </div>
                </div>
                {clickedEmailOptions?.cc && (
                  <TextInput name="cc" fullWidth value={formData.cc}>
                    Cc
                  </TextInput>
                )}
                {clickedEmailOptions?.bcc && (
                  <TextInput name="bcc" fullWidth>
                    Bcc
                  </TextInput>
                )}
                <TextInput name="subject" required fullWidth>
                  Subject
                </TextInput>
                <TextAreaInput name="message" required fullWidth>
                  Message
                </TextAreaInput>
              </div>
            </div>
            {(fromTab === "caregiverFinance" ||
              fromTab === "patientFinance") && (
              <div className="px-4">
                <InvoiceModal
                  showIconWithText
                  invoiceId={invoiceId}
                  resetKey={"123"}
                  invoiceStartDate={invoiceStartDate}
                  invoiceEndDate={invoiceEndDate}
                  yearMonth={invoiceYearMonth?.toISOString()}
                  pageTitle={
                    fromTab === "patientFinance"
                      ? "patient-finance"
                      : "caregiver-finance"
                  }
                />
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="submit"
            loading={isLoading}
            appearance="primary"
            onClick={handleSubmit}
            disabled={isDisabled}
          >
            Send Mail
          </Button>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            type="reset"
            appearance="subtle"
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
